import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { useProfile } from '@/features/auth/useProfile';
import { formatBudget, formatRelative } from '@/lib/format';
import { LEAD_SOURCES, LEAD_STAGES, type Lead, type LeadSource, type LeadStage } from '@/types/app';
import { CreateLeadDialog } from './CreateLeadDialog';
import { ImportLeadsDialog } from './ImportLeadsDialog';
import { SlaCountdown } from './SlaCountdown';
import { SlaBadge, StageBadge, TemperatureBadge } from './StageBadge';
import { PAGE_SIZE, useLeads, type LeadFilters } from './useLeads';

export default function LeadsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<LeadFilters>({});
  const [page, setPage] = useState(0);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const { data, isPending, isError, isPlaceholderData } = useLeads(filters, page);
  const { data: me } = useProfile();
  const canImport =
    !!me && ['sales_manager', 'admin', 'marketing', 'team_leader'].includes(me.role);

  const setFilter = (patch: Partial<LeadFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(0); // a new filter invalidates the page number
  };

  const total = data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  const onCreated = (lead: Lead, wasDuplicate: boolean) => {
    // #3 surfaced honestly: tell them it already existed and who owns it, rather
    // than silently opening a record they think they just made.
    setNotice(wasDuplicate ? t('leads.duplicateNotice', { name: lead.full_name }) : null);
    void navigate(`/leads/${lead.id}`);
  };

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold text-slate-900">{t('nav.leads')}</h1>
        <div className="flex gap-2">
          {canImport ? (
            <Button variant="secondary" onClick={() => setImporting(true)}>
              {t('leads.import.title')}
            </Button>
          ) : null}
          <Button onClick={() => setCreating(true)}>{t('leads.create')}</Button>
        </div>
      </div>

      {notice ? <Alert tone="info">{notice}</Alert> : null}
      {isError ? <Alert tone="error">{t('common.error')}</Alert> : null}

      <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:grid-cols-2 lg:grid-cols-4">
        <Input
          label={t('leads.search')}
          placeholder={t('leads.searchHint')}
          value={filters.search ?? ''}
          onChange={(e) => setFilter({ search: e.target.value })}
        />
        <Select
          label={t('leads.stage')}
          placeholder={t('leads.allStages')}
          value={filters.stage ?? ''}
          onChange={(e) => setFilter({ stage: e.target.value as LeadStage | '' })}
          options={LEAD_STAGES.map((s) => ({
            value: s,
            label: t(`stages.${s}` as 'stages.new'),
          }))}
        />
        <Select
          label={t('leads.source')}
          placeholder={t('leads.allSources')}
          value={filters.source ?? ''}
          onChange={(e) => setFilter({ source: e.target.value as LeadSource | '' })}
          options={LEAD_SOURCES.map((s) => ({
            value: s,
            label: t(`sources.${s}` as 'sources.meta'),
          }))}
        />
        <Select
          label={t('leads.slaStatus')}
          placeholder={t('leads.allSla')}
          value={filters.slaStatus ?? ''}
          onChange={(e) => setFilter({ slaStatus: e.target.value })}
          options={[
            { value: 'pending', label: t('slaStatuses.pending') },
            { value: 'met', label: t('slaStatuses.met') },
            { value: 'breached', label: t('slaStatuses.breached') },
          ]}
        />
      </div>

      {isPending ? (
        <Spinner />
      ) : total === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          {t('leads.empty')}
        </div>
      ) : (
        <>
          <div
            className={`overflow-x-auto rounded-xl border border-slate-200 bg-white ${
              isPlaceholderData ? 'opacity-60' : ''
            }`}
          >
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('leads.fullName')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('leads.stage')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('leads.temperature')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('leads.budget')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('leads.slaStatus')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-start">
                    {t('leads.lastActivity')}
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {(data?.rows ?? []).map((lead) => (
                  <tr key={lead.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <Link
                        to={`/leads/${lead.id}`}
                        className="font-medium text-brand-800 hover:underline"
                      >
                        {lead.full_name}
                      </Link>
                      <div className="flex items-center gap-1 text-xs text-slate-500" dir="ltr">
                        <Phone className="size-3" aria-hidden="true" />
                        {lead.phone_primary}
                      </div>
                      {lead.duplicate_hits > 0 ? (
                        <div className="text-xs text-amber-700">
                          {t('leads.duplicateCount', { count: lead.duplicate_hits })}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <StageBadge stage={lead.stage} />
                    </td>
                    <td className="px-3 py-2">
                      <TemperatureBadge temperature={lead.temperature} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-slate-700" dir="ltr">
                      {formatBudget(lead.budget_min, lead.budget_max)}
                    </td>
                    <td className="px-3 py-2">
                      {/* A live countdown while the clock is running, the settled
                          outcome once it has stopped. */}
                      {lead.sla_status === 'pending' ? (
                        <SlaCountdown dueAt={lead.sla_due_at} status={lead.sla_status} />
                      ) : (
                        <SlaBadge status={lead.sla_status} />
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                      {lead.last_activity_at ? (
                        formatRelative(lead.last_activity_at)
                      ) : (
                        <span className="text-amber-700">{t('leads.neverContacted')}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-600">
            <span>
              {t('leads.showing', {
                from: page * PAGE_SIZE + 1,
                to: Math.min((page + 1) * PAGE_SIZE, total),
                total,
              })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                {t('common.previous')}
              </Button>
              <Button
                variant="secondary"
                disabled={page >= lastPage}
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>
        </>
      )}

      <ImportLeadsDialog open={importing} onClose={() => setImporting(false)} />

      <CreateLeadDialog open={creating} onClose={() => setCreating(false)} onCreated={onCreated} />
    </section>
  );
}
